import {
  DatePicker,
  LoadingLoop,
  Modal,
  Select,
  TextArea,
  TextInput,
} from '@medeloop/medeloop-components-library';
import dayjs from 'dayjs';
import { ChangeEvent, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { CreateStudyDTO, createStudy } from 'src/api/studies';
import { getResearcherOrganizations } from 'src/api/users';
import { useOrganizationDetailsContext } from 'src/components/organizations/organization-details';

import { selectAccount, selectAuthToken } from 'src/store/selectors';
import { OrganizationResearcher } from 'src/types/organizations';

export function CreateStudyPage() {
  const token = useSelector(selectAuthToken);
  const account = useSelector(selectAccount);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const { container } = useOrganizationDetailsContext();

  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');

  const [expectedParticipants, setExpectedParticipants] = useState(1);
  const handleExpectedParticipants = (val: ChangeEvent<HTMLInputElement>) => {
    const value = val.target.value;
    if (typeof Number(value) === 'number') {
      setExpectedParticipants(Number(value));
    }
  };

  const [expectedDurationNumber, setExpectedDurationNumber] = useState(1);
  const handleExpectedDurationNumber = (val: ChangeEvent<HTMLInputElement>) => {
    const value = val.target.value;
    if (typeof Number(value) === 'number') {
      setExpectedDurationNumber(Number(value));
    }
  };

  const [expectedDurationPeriod, setExpectedDurationPeriod] = useState('weeks');

  const [startDate, setStartDate] = useState<Date | undefined>(undefined);
  const handleStartDate = (val: Date | undefined) => {
    if (val) {
      if (completionDate && val > completionDate) {
        setStartDate(val);
        setCompletionDate(val);
      } else {
        setStartDate(val);
      }
    } else {
      setStartDate(val);
    }
  };

  const [completionDate, setCompletionDate] = useState<Date | undefined>(undefined);
  const handleCompletionDate = (val: Date | undefined) => {
    if (val) {
      if (startDate && val < startDate) {
        setStartDate(val);
        setCompletionDate(val);
      } else {
        setCompletionDate(val);
      }
    } else {
      setCompletionDate(val);
    }
  };
  const [description, setDescription] = useState('');

  const { organizationId } = useParams();

  const [organizationName, setOrganizationName] = useState('');

  const [error, setError] = useState('');

  const selectOrganization = (data: { organizations: OrganizationResearcher[] }) => {
    const foundOrg = data.organizations.find(
      (item: OrganizationResearcher) => item.organizationId === organizationId,
    );
    if (foundOrg) {
      setOrganizationName(foundOrg.organizationName);
    }
    return data;
  };

  const organizationsQuery = useQuery({
    queryKey: ['researcher', account.researcherId, 'organizations'],
    queryFn: () => {
      return getResearcherOrganizations({ token, researcherId: account.researcherId });
    },
    select: selectOrganization,
  });

  const createStudyMutation = useMutation({
    mutationFn: (newStudy: CreateStudyDTO) => {
      return createStudy(token, newStudy);
    },
    onSuccess: () => {
      navigate('/overview');
    },
    onError: (error: Error) => {
      setError(error.message);
    },
  });

  const handleSubmit = () => {
    createStudyMutation.mutate({
      name,
      displayName,
      description,
      organizationId: organizationId || '',
      startDate: dayjs(startDate).format('YYYY-MM-DD'),
      completionDate: dayjs(completionDate).format('YYYY-MM-DD'),
      expectedParticipants: expectedParticipants,
      expectedParticipantDuration: `${expectedDurationNumber} ${expectedDurationPeriod}`,
    });
  };

  return (
    <Modal
      title={t('create_study_for', { name: organizationName || '...' })}
      // isLoading={organizationsQuery.isLoading || createStudyMutation.isLoading}
      isOpen
      actionType="primary"
      actionText={t('save')}
      cancelText={t('cancel')}
      actionFunction={handleSubmit}
      container={container}
      onCancel={() => navigate(-1)}
    >
      {createStudyMutation.isPending ? (
        <div className="flex items-center justify-center w-[707px] h-96">
          <LoadingLoop className="w-16" />
        </div>
      ) : (
        <div className="flex flex-col w-[707px] text-sm">
          {organizationsQuery.isError && (
            <span className="pb-2 text-red-300">{t('error_fetching_organizations')}</span>
          )}

          <TextInput
            label={t('study_name')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeHolder="Ex. Complex regional pain symptom"
          />
          <TextInput
            label={t('display_name_header')}
            value={displayName}
            onChange={(e) => setDisplayName(e.target.value)}
            placeHolder="Ex. CRPS"
          />
          <div className="flex gap-4">
            <div className="flex flex-1">
              <TextInput
                label={t('expected_participants')}
                value={expectedParticipants}
                onChange={handleExpectedParticipants}
                type="number"
                placeHolder={t('example_num')}
              />
            </div>
            <div className="flex flex-1 flex-col pt-2">
              <span>{t('expected_participant_duration_in_study')}</span>
              <div className="flex gap-2">
                <TextInput
                  value={expectedDurationNumber}
                  onChange={handleExpectedDurationNumber}
                  type="number"
                  placeHolder={t('example_num')}
                  max={100}
                  min={1}
                />
                <Select
                  options={[
                    { value: 'weeks', label: t('weeks') },
                    { value: 'days', label: t('days') },
                  ]}
                  value={expectedDurationPeriod}
                  onValueChange={(val: string) => setExpectedDurationPeriod(val)}
                  className="!w-[151px] mt-2"
                />
              </div>
            </div>
          </div>
          <div className="flex gap-4">
            <div className="flex-1">
              <DatePicker
                label={t('start_date')}
                value={startDate}
                onSelect={handleStartDate}
                placeholder="YYYY-MM-DD"
                hasIcon
              />
            </div>
            <div className="flex-1">
              <DatePicker
                label={t('completion_date')}
                value={completionDate}
                onSelect={handleCompletionDate}
                placeholder="YYYY-MM-DD"
                hasIcon
              />
            </div>
          </div>
          <TextArea
            label={t('description')}
            value={description}
            placeHolder={t('enter_study_description')}
            onChange={(e) => setDescription(e.target.value)}
          />
        </div>
      )}
      {error && (
        <span className="text-red-500">
          {t('error')}: {error}
        </span>
      )}
    </Modal>
  );
}
